import { z } from "zod";
import { ClosedPeriod } from "./closed-periods.ts";
import { I18nText, Id, IsoDate, LatLon, Slug, Status, Timestamp } from "./common.ts";
import { DiveObject } from "./objects.ts";

export const DIFFICULTIES = ["very_easy", "easy", "medium", "hard", "very_hard"] as const;
export const Difficulty = z.enum(DIFFICULTIES);
export type Difficulty = z.infer<typeof Difficulty>;

const Rating = z.number().int().min(0).max(5).nullable();

export const SpotRatings = z
  .object({
    fish: Rating,
    walls: Rating,
    caves: Rating,
    wrecksSculptures: Rating,
    current: Rating,
    surroundings: Rating,
  })
  .strict();
export type SpotRatings = z.infer<typeof SpotRatings>;

export const EMPTY_RATINGS: SpotRatings = {
  fish: null,
  walls: null,
  caves: null,
  wrecksSculptures: null,
  current: null,
  surroundings: null,
};

/**
 * A spot closed for good (diving banned, access gone), e.g. "Frau Tschanz". Unlike
 * `closedPeriods` it never ends. The spot stays visible with a "closed for good" mark so divers
 * know not to go; `null` = not closed. Set by staff only (not part of diver suggestions).
 */
export const PermanentClosure = z
  .object({
    /** Day the closure took effect, if known. */
    since: IsoDate.nullable(),
    reason: I18nText,
  })
  .strict();
export type PermanentClosure = z.infer<typeof PermanentClosure>;

/** Lean shape for the map (GET /api/spots). */
const SpotListItemBase = z
  .object({
    id: Id,
    slug: Slug,
    name: z.string().min(1).max(120),
    /** Entry point. */
    location: LatLon,
    lakeSlug: Slug.nullable(),
    /** True only if the spot's lake has bathy AND the entry point has a bathy/shore value. */
    hasBathy: z.boolean(),
    waterBody: z.string().max(120),
    locality: z.string().max(120),
    difficulty: Difficulty.nullable(),
    maxDepthM: z.number().min(0).max(400).nullable(),
    closedPeriods: z.array(ClosedPeriod),
    permanentClosure: PermanentClosure.nullable(),
    objectCount: z.number().int().min(0),
    status: Status,
  })
  .strict();
const hasBathyNeedsLake = (s: { hasBathy: boolean; lakeSlug: string | null }) =>
  !s.hasBathy || s.lakeSlug !== null;
const hasBathyNeedsLakeError = { message: "hasBathy requires lakeSlug", path: ["hasBathy"] };
export const SpotListItem = SpotListItemBase.refine(hasBathyNeedsLake, hasBathyNeedsLakeError);
export type SpotListItem = z.infer<typeof SpotListItem>;

export const SpotList = z.object({ spots: z.array(SpotListItem) }).strict();
export type SpotList = z.infer<typeof SpotList>;

const spotContent = {
  parkingLocation: LatLon.nullable(),
  description: I18nText,
  access: I18nText,
  parking: I18nText,
  entry: I18nText,
  hazards: I18nText,
  restrictions: I18nText,
  facilities: I18nText,
  ratings: SpotRatings,
};

/** GET /api/spots/:slug. Objects: published only for the public, all for moderators. */
export const SpotDetail = SpotListItemBase.extend({
  ...spotContent,
  objects: z.array(DiveObject),
  createdAt: Timestamp,
  updatedAt: Timestamp,
})
  .strict()
  .refine(hasBathyNeedsLake, hasBathyNeedsLakeError);
export type SpotDetail = z.infer<typeof SpotDetail>;

/**
 * POST /api/spots. Server derives: id, slug (from name, unique), lakeSlug, hasBathy,
 * objectCount, timestamps. `slug` may be given to override the derived one.
 */
export const SpotCreate = z
  .object({
    name: z.string().trim().min(1).max(120),
    slug: Slug.optional(),
    location: LatLon,
    waterBody: z.string().max(120).default(""),
    locality: z.string().max(120).default(""),
    difficulty: Difficulty.nullable().default(null),
    maxDepthM: z.number().min(0).max(400).nullable().default(null),
    closedPeriods: z.array(ClosedPeriod).max(12).default([]),
    permanentClosure: PermanentClosure.nullable().default(null),
    status: Status.default("draft"),
    parkingLocation: LatLon.nullable().default(null),
    description: I18nText.default({}),
    access: I18nText.default({}),
    parking: I18nText.default({}),
    entry: I18nText.default({}),
    hazards: I18nText.default({}),
    restrictions: I18nText.default({}),
    facilities: I18nText.default({}),
    ratings: SpotRatings.default(EMPTY_RATINGS),
  })
  .strict();
export type SpotCreate = z.input<typeof SpotCreate>;

/**
 * PATCH /api/spots/:id — any subset of the create fields. Every top-level key REPLACES the
 * stored value whole (I18nText objects and `ratings` included — send all locales / all six
 * ratings). `{}` is a no-op: 200 with the unchanged spot, no audit row.
 */
export const SpotUpdate = z
  .object({
    name: z.string().trim().min(1).max(120),
    slug: Slug,
    location: LatLon,
    waterBody: z.string().max(120),
    locality: z.string().max(120),
    difficulty: Difficulty.nullable(),
    maxDepthM: z.number().min(0).max(400).nullable(),
    closedPeriods: z.array(ClosedPeriod).max(12),
    permanentClosure: PermanentClosure.nullable(),
    status: Status,
    ...spotContent,
  })
  .partial()
  .strict();
export type SpotUpdate = z.infer<typeof SpotUpdate>;
